import { type FuzzyIndex, buildFuzzyIndex } from '@/helper/fuzzy';
import { getAddresses } from '@/helper/joules';
import { STREET_NORMALIZERS, findCanonicalStreet, searchStreetsLocal } from '@/helper/streetSearch';

export type StreetLocation = {
  postcode: string;
  city: string;
};

export type StreetCatalog = {
  streets: string[];
  index: FuzzyIndex<string>;
};

const catalogRequests = new Map<string, Promise<StreetCatalog>>();

export function getStreetLocationKey({ postcode, city }: StreetLocation): string {
  return `${postcode.trim()}|${city.trim().toLocaleLowerCase('de')}`;
}

export function isCompleteStreetLocation(location: StreetLocation): boolean {
  return /^\d{5}$/.test(location.postcode.trim()) && location.city.trim().length > 0;
}

export function loadStreetCatalog(location: StreetLocation): Promise<StreetCatalog> {
  const key = getStreetLocationKey(location);
  const existingRequest = catalogRequests.get(key);
  if (existingRequest) return existingRequest;

  const request = getAddresses(location.postcode, location.city, '', -1)
    .then((streets) => ({
      streets,
      index: buildFuzzyIndex(streets, { normalizers: STREET_NORMALIZERS }),
    }))
    .catch((error) => {
      catalogRequests.delete(key);
      throw error;
    });
  catalogRequests.set(key, request);
  return request;
}

export function preloadStreetCatalog(location: StreetLocation): void {
  if (!isCompleteStreetLocation(location)) return;
  void loadStreetCatalog(location).catch(() => undefined);
}

export function suggestStreets(catalog: StreetCatalog | undefined, query: string): string[] {
  if (!catalog || !query.trim()) return [];
  return searchStreetsLocal(query, catalog);
}

export function resolveStreet(catalog: StreetCatalog | undefined, value: string): string | null {
  if (!catalog) return null;
  return findCanonicalStreet(value, catalog.streets);
}

export async function resolveStreetFromCatalog(location: StreetLocation, value: string): Promise<string | null> {
  if (!isCompleteStreetLocation(location) || !value.trim()) return null;
  const catalog = await loadStreetCatalog(location);
  return resolveStreet(catalog, value);
}

export async function isStreetInCatalog(location: StreetLocation, value: string): Promise<boolean> {
  try {
    return Boolean(await resolveStreetFromCatalog(location, value));
  } catch {
    // The authoritative server-side submission check owns availability failures.
    // Do not mislabel a valid user entry as an invalid street when the catalog is down.
    return true;
  }
}

export function clearStreetCatalogCache(): void {
  catalogRequests.clear();
}
